import React, { useRef, useState } from "react";

const SITE = {
    name: "COMPASSIA",
    domain: "https://compassia.es",
    email: "info@compassia.es",
    phone: "+34 600 000 000",
    city: "Barcelona",
    country: "España",
    tagline: "Optimización sin fricción",
    description:
        "Automatizamos y optimizamos procesos con tu stack actual. Pilotos de 2–4 semanas con objetivos medibles. Integraciones n8n, RAG privado en AWS, Leads/CRM, contratos y aprobaciones, onboarding/offboarding y reporting.",
    keywords:
        "automatización PYMES, n8n, RAG privado, AWS, NetSuite, integración ERP CRM, IA aplicada, dashboards KPIs, gestión de agenda, clasificación de leads, firma electrónica, aprobaciones, onboarding, reporting, proyectos a medida",
    ogImage: "/og-compassia.jpg",
    twitter: "@compassia",
};

const API_URL = `${SITE.domain}/api/send-request.php`;

export function CTA() {
    const [status, setStatus] = useState({ state: "idle", message: "" });
    const [serverMsg, setServerMsg] = useState("");
    const [consent, setConsent] = useState(false);
    const [consentTouched, setConsentTouched] = useState(false);
    const [invalid, setInvalid] = useState({ name: false, email: false, message: false });
    const consentRef = useRef(null);

    const onSubmit = async (e) => {
        e.preventDefault();
        setServerMsg("");

        const form = e.currentTarget;
        const data = new FormData(form);

        const name = (data.get("name") || "").toString().trim();
        const email = (data.get("email") || "").toString().trim();
        const company = (data.get("company") || "").toString().trim();
        const message = (data.get("message") || "").toString().trim();
        const hp = (data.get("hp") || "").toString();
        const consentValue = data.get("privacy") === "yes";

        const nextInvalid = {
            name: !name,
            email: !email,
            message: !message,
        };

        // Validaciones básicas de campos
        if (nextInvalid.name || nextInvalid.email || nextInvalid.message) {
            setInvalid(nextInvalid);
            setStatus({
                state: "error",
                message: "Revisa los campos obligatorios (nombre, email, mensaje).",
            });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
        if (!emailRegex.test(email)) {
            setInvalid((p) => ({ ...p, email: true }));
            setStatus({
                state: "error",
                message: "Introduce un email válido.",
            });
            return;
        }

        // Requisito legal RGPD: consentimiento expreso antes de enviar
        if (!consentValue) {
            setStatus({
                state: "error",
                message: "Debes aceptar la Política de Privacidad para continuar.",
            });
            setConsentTouched(true);
            consentRef.current?.focus();
            return;
        }

        setStatus({ state: "loading", message: "Enviando..." });

        const subject = "Quiero un diagnóstico exprés – COMPASSIA";
        const text =
            `Nueva solicitud desde ${SITE.name}\n\n` +
            `Nombre: ${name}\n` +
            `Email: ${email}\n` +
            (company ? `Empresa: ${company}\n` : "") +
            `Consentimiento privacidad: ${consentValue ? "sí" : "no"}\n` +
            `\nMensaje:\n${message}\n`;

        try {
            const res = await fetch(API_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                    "X-Requested-With": "fetch",
                },
                body: JSON.stringify({
                    subject,
                    text,
                    replyTo: email,
                    hp,
                }),
            });

            const json = await res.json().catch(() => ({}));

            if (!res.ok || json.ok !== true) {
                const msg =
                    json?.error ||
                    (res.status === 429
                        ? "Demasiadas solicitudes. Espera un momento."
                        : res.status === 400
                            ? "Datos inválidos. Revisa el formulario."
                            : "No se pudo enviar. Inténtalo más tarde.");
                setStatus({ state: "error", message: msg });
                setServerMsg(`(${res.status})`);
                return;
            }

            setStatus({
                state: "success",
                message: "¡Listo! Te contactaremos en breve.",
            });
            setServerMsg("");
            form.reset();
            setConsent(false);
            setConsentTouched(false);
            setInvalid({ name: false, email: false, message: false });
        } catch (err) {
            setStatus({
                state: "error",
                message: "No se pudo enviar. Comprueba tu conexión e inténtalo de nuevo.",
            });
            setServerMsg("");
        }
    };

    const isLoading = status.state === "loading";
    const privacyUrl = `${SITE.domain}/politica-privacidad`;

    // Helpers de estilo coherentes con tus variables de marca
    const invalidStyle = (isInvalid) =>
        isInvalid
            ? {
                borderColor: "var(--error-color)",
                boxShadow: "0 0 0 4px rgba(214,69,69,.18)",
            }
            : undefined;

    const statusStyle =
        status.state === "success"
            ? { background: "#ecf8ee", color: "var(--success-color)", border: "1px solid rgba(0,0,0,.06)" }
            : status.state === "error"
                ? { background: "#fff4f4", color: "var(--error-color)", border: "1px solid rgba(0,0,0,.06)" }
                : status.state === "loading"
                    ? { background: "#f4f6fb", color: "var(--info-color)", border: "1px solid rgba(0,0,0,.06)" }
                    : { background: "transparent" };

    return (
        <div id="contacto">
            <div
                className="cta-inner card"
                style={{ padding: 24, borderRadius: "var(--radius-lg)" }}
            >
                <div className="cta-grid" style={{ display: "grid", gap: 18 }}>
                    {/* Cabecera */}
                    <div className="cta-copy">
                        <p className="eyebrow strong">¿Listo para acelerar?</p>
                        <h3 className="h3-xl">Diagnóstico exprés gratuito</h3>
                        <p className="muted">
                            Cuéntanos tu stack y tus cuellos de botella. Te proponemos un plan de
                            impacto rápido para optimizar costes y tiempo.
                        </p>
                    </div>

                    {/* Formulario */}
                    <form className="form" onSubmit={onSubmit} noValidate>
                        {/* Honeypot anti-bots */}
                        <div style={{ position: "absolute", left: "-5000px" }} aria-hidden="true">
                            <label>
                                No rellenar:
                                <input
                                    name="hp"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    aria-hidden="true"
                                />
                            </label>
                        </div>

                        <div className="form-row">
                            <input
                                name="name"
                                required
                                placeholder="Tu nombre"
                                disabled={isLoading}
                                autoComplete="name"
                                aria-invalid={invalid.name ? "true" : "false"}
                                style={invalidStyle(invalid.name)}
                                onInput={(e) =>
                                    setInvalid((p) => ({ ...p, name: !(e.currentTarget.value || "").trim() }))
                                }
                            />
                            <input
                                type="email"
                                name="email"
                                required
                                placeholder="Email de contacto"
                                disabled={isLoading}
                                autoComplete="email"
                                inputMode="email"
                                aria-invalid={invalid.email ? "true" : "false"}
                                style={invalidStyle(invalid.email)}
                                onInput={(e) =>
                                    setInvalid((p) => ({ ...p, email: !(e.currentTarget.value || "").trim() }))
                                }
                            />
                        </div>

                        <input
                            name="company"
                            placeholder="Empresa (opcional)"
                            disabled={isLoading}
                            autoComplete="organization"
                        />

                        <textarea
                            name="message"
                            required
                            rows={4}
                            placeholder="¿Qué quieres optimizar? ERP, CRM, operaciones, soporte, reporting…"
                            disabled={isLoading}
                            aria-invalid={invalid.message ? "true" : "false"}
                            style={invalidStyle(invalid.message)}
                            onInput={(e) =>
                                setInvalid((p) => ({ ...p, message: !(e.currentTarget.value || "").trim() }))
                            }
                        />

                        {/* Consentimiento RGPD */}
                        <div
                            className="mini-card"
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 10,
                                background: "#fff",
                            }}
                        >
                            <input
                                ref={consentRef}
                                type="checkbox"
                                name="privacy"
                                value="yes"
                                required
                                checked={consent}
                                onChange={(e) => setConsent(e.target.checked)}
                                onBlur={() => setConsentTouched(true)}
                                disabled={isLoading}
                                aria-invalid={consentTouched && !consent ? "true" : "false"}
                                aria-describedby="privacy-error"
                                style={{
                                    marginTop: 2,
                                    width: 18,
                                    height: 18,
                                    accentColor: "var(--primary-color)",
                                }}
                            />
                            <label style={{ lineHeight: 1.4 }}>
                                He leído y acepto la{" "}
                                <a
                                    href={privacyUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link-primary"
                                >
                                    Política de Privacidad
                                </a>
                                .
                                <span className="fineprint" style={{ display: "block", marginTop: 4 }}>
                                    Necesaria para enviarnos tu solicitud.
                                </span>
                            </label>
                        </div>
                        {consentTouched && !consent && (
                            <p
                                id="privacy-error"
                                className="small"
                                role="alert"
                                style={{
                                    marginTop: -6,
                                    color: "var(--error-color)",
                                    fontWeight: 600,
                                }}
                            >
                                Debes aceptar la Política de Privacidad para enviar el formulario.
                            </p>
                        )}

                        <div className="actions" style={{ marginTop: 4 }}>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={isLoading || !consent}
                            >
                                {isLoading ? "Enviando…" : "Reservar diagnóstico"}
                            </button>
                        </div>

                        {/* Banner de estado homogéneo */}
                        <div
                            role="status"
                            aria-live="polite"
                            className="form-status"
                            style={{
                                ...statusStyle,
                                minHeight: "44px",
                                marginTop: 8,
                                borderRadius: "var(--radius)",
                                padding: status.message ? "10px 12px" : 0,
                                boxShadow: "var(--shadow-sm)",
                                display: status.message ? "flex" : "block",
                                alignItems: "center",
                                gap: 8,
                                fontWeight: 600,
                            }}
                        >
                            {status.message && (
                                <>
                                    {/* Indicador sutil */}
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: 999,
                                            background:
                                                status.state === "success"
                                                    ? "var(--success-color)"
                                                    : status.state === "error"
                                                        ? "var(--error-color)"
                                                        : status.state === "loading"
                                                            ? "var(--info-color)"
                                                            : "transparent",
                                            boxShadow: "0 0 0 4px rgba(0,0,0,.04)",
                                        }}
                                    />
                                    <span>
                                        {status.message}{" "}
                                        {serverMsg && (
                                            <span className="muted" style={{ opacity: 0.7 }}>
                                                {serverMsg}
                                            </span>
                                        )}
                                    </span>
                                </>
                            )}
                        </div>

                        {/* Nota legal breve bajo el form */}
                        <p className="fineprint">
                            Usaremos tus datos solo para responder tu solicitud. Más información en la{" "}
                            <a
                                href={privacyUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link-primary"
                            >
                                Política de Privacidad
                            </a>
                            . No compartimos tus datos con terceros.
                        </p>
                    </form>
                </div>

                {/* Blobs decorativos suaves (coherentes con tu look & feel) */}
                <span className="brand-blob brand-blob--red brand-blob--top-left" />
                <span className="brand-blob brand-blob--orange brand-blob--top-right" />
            </div>
        </div>
    );
}
